import moment, {type Moment} from 'moment';
import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {ESTIMATION_UNIT, type EstimationUnit} from '../../../../constants';
import Util from '../../util/util';

type ParamValues = {
	oldValue?: string;
	newValue?: string;
	oldSprintName?: string | 'RECORD_DELETED';
	newSprintName?: string | 'RECORD_DELETED';
	oldPhaseName?: string | 'RECORD_DELETED';
	newPhaseName?: string | 'RECORD_DELETED';
	oldStatusColumnName?: string | 'RECORD_DELETED';
	newStatusColumnName?: string | 'RECORD_DELETED';
	oldProjectName?: string | 'RECORD_DELETED';
	newProjectName?: string | 'RECORD_DELETED';
	oldMinutesRegistered?: number;
	newMinutesRegistered?: number;
	oldDate?: Date;
	newDate?: Date;
};

type TaskActivityMessageProps = {
	eventType: string;
	messageParams: ParamValues & {
		personName?: string;
		labelName?: string;
	};
	params: ParamValues & {
		value?: string;
		estimationUnit?: EstimationUnit;
		labelName?: string | 'RECORD_DELETED';
		personName?: string | 'RECORD_DELETED';
		timeRegPersonName?: string | 'RECORD_DELETED';
		date?: Date;
		done?: boolean;
		minutesRegistered?: number;
	};
	taskLevelSuffix: unknown;
};

export function TaskActivityMessage({
	eventType,
	messageParams,
	params,
	taskLevelSuffix,
}: TaskActivityMessageProps): React.JSX.Element {
	const intl = useIntl();

	// How has this ever worked?
	const formatMoment = (date: Moment): string => intl.formatDate(date as unknown as Date);

	if (eventType.startsWith('TODO_OR_SUB_TASK')) {
		eventType += taskLevelSuffix;
	}
	switch (eventType) {
		case 'CARD_UPDATE_TITLE':
			messageParams.oldValue = params.oldValue ? params.oldValue : intl.formatMessage({id: 'task_modal.untitled'});
			break;
		case 'ESTIMATE_UPDATE':
			if (params.estimationUnit === ESTIMATION_UNIT.HOURS) {
				messageParams.oldValue = Util.convertMinutesToFullHour(params.oldValue, intl);
				messageParams.newValue = Util.convertMinutesToFullHour(params.newValue, intl);
				return (
					<div>
						<FormattedMessage id="task_activity_log.ESTIMATE_UPDATE_MINUTES" values={messageParams} />
					</div>
				);
			} else {
				return (
					<div>
						<FormattedMessage id="task_activity_log.ESTIMATE_UPDATE_POINTS" values={messageParams} />
					</div>
				);
			}
		case 'START_DATE_ADD':
			return (
				<div>
					<FormattedMessage
						id={'task_activity_log.' + eventType}
						values={{date: formatMoment(moment(params.value, 'YYYY-MM-DD'))}}
					/>
				</div>
			);
		case 'START_DATE_REMOVE':
			return (
				<div>
					<FormattedMessage
						id={'task_activity_log.' + eventType}
						values={{date: formatMoment(moment(params.value, 'YYYY-MM-DD'))}}
					/>
				</div>
			);
		case 'START_DATE_UPDATE':
			return (
				<div>
					<FormattedMessage
						id={'task_activity_log.' + eventType}
						values={{
							oldDate: formatMoment(moment(params.oldValue, 'YYYY-MM-DD')),
							newDate: formatMoment(moment(params.newValue, 'YYYY-MM-DD')),
						}}
					/>
				</div>
			);
		case 'DEADLINE_ADD':
			return (
				<div>
					<FormattedMessage
						id={'task_activity_log.' + eventType}
						values={{date: formatMoment(moment(params.value, 'YYYY-MM-DD'))}}
					/>
				</div>
			);
		case 'DEADLINE_REMOVE':
			return (
				<div>
					<FormattedMessage
						id={'task_activity_log.' + eventType}
						values={{date: formatMoment(moment(params.value, 'YYYY-MM-DD'))}}
					/>
				</div>
			);
		case 'DEADLINE_UPDATE':
			return (
				<div>
					<FormattedMessage
						id={'task_activity_log.' + eventType}
						values={{
							oldDate: formatMoment(moment(params.oldValue, 'YYYY-MM-DD')),
							newDate: formatMoment(moment(params.newValue, 'YYYY-MM-DD')),
						}}
					/>
				</div>
			);
		case 'CHANGE_SPRINT':
			messageParams = {
				oldValue: params.oldSprintName
					? params.oldSprintName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.oldSprintName
					: intl.formatMessage({id: 'project_sprints.backlog'}),
				newValue: params.newSprintName
					? params.newSprintName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.newSprintName
					: intl.formatMessage({id: 'project_sprints.backlog'}),
			};
			break;
		case 'CHANGE_SCOPE_GROUP':
			messageParams = {
				oldValue: params.oldPhaseName
					? params.oldPhaseName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.oldPhaseName
					: intl.formatMessage({id: 'project_scopes.no-scope'}),
				newValue: params.newPhaseName
					? params.newPhaseName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.newPhaseName
					: intl.formatMessage({id: 'project_scopes.no-scope'}),
			};
			break;
		case 'CHANGE_STATUS_COLUMN':
			messageParams = {
				oldValue:
					params.oldStatusColumnName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.oldStatusColumnName,
				newValue:
					params.newStatusColumnName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.newStatusColumnName,
			};
			break;
		case 'PROJECT_CHANGE':
			messageParams = {
				oldValue:
					params.oldProjectName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.oldProjectName,
				newValue:
					params.newProjectName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.newProjectName,
			};
			break;
		case 'REPORTED_TIME_ADD': {
			const personName =
				params.timeRegPersonName === 'RECORD_DELETED'
					? intl.formatMessage({id: 'task_activity_log.record-deleted'})
					: params.timeRegPersonName;
			return (
				<div>
					<FormattedMessage
						id={'task_activity_log.REPORTED_TIME_ADD' + (params.timeRegPersonName ? '_WITH_PERSON' : '')}
						values={{
							value: Util.convertMinutesToFullHour(params.minutesRegistered, intl),
							date: formatMoment(moment(params.date, 'YYYY-MM-DD')),
							personName,
						}}
					/>
				</div>
			);
		}
		case 'REPORTED_TIME_UPDATE_DATE':
			return (
				<div>
					<FormattedMessage
						id="task_activity_log.REPORTED_TIME_UPDATE_DATE"
						values={{
							oldDate: formatMoment(moment(params.oldDate, 'YYYY-MM-DD')),
							newDate: formatMoment(moment(params.newDate, 'YYYY-MM-DD')),
						}}
					/>
				</div>
			);
		case 'REPORTED_TIME_UPDATE_TIME': {
			const oldHours = Util.convertMinutesToFullHour(params.oldMinutesRegistered, intl);
			const newHours = Util.convertMinutesToFullHour(params.newMinutesRegistered, intl);

			return (
				<div>
					<FormattedMessage
						id="task_activity_log.REPORTED_TIME_UPDATE_TIME"
						values={{
							oldHours,
							newHours,
							date: formatMoment(moment(params.date, 'YYYY-MM-DD')),
						}}
					/>
				</div>
			);
		}
		case 'REPORTED_TIME_DELETE':
			return (
				<div>
					<FormattedMessage
						id="task_activity_log.REPORTED_TIME_DELETE"
						values={{
							hours: Util.convertMinutesToFullHour(params.minutesRegistered, intl),
							date: formatMoment(moment(params.date, 'YYYY-MM-DD')),
						}}
					/>
				</div>
			);
		case 'TODO_OR_SUB_TASK_UPDATE_STATE.SUBTASK':
		case 'TODO_OR_SUB_TASK_UPDATE_STATE.TODO': {
			const completionSuffix = params.done ? '.COMPLETE' : '.OPEN';
			return (
				<div>
					<FormattedMessage id={'task_activity_log.' + eventType + completionSuffix} values={messageParams} />
				</div>
			);
		}
		case 'LABEL_ADD':
		case 'LABEL_REMOVE':
			messageParams = {
				labelName:
					params.labelName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.labelName,
			};
			break;
		case 'PERSON_ASSIGNED':
		case 'PERSON_UNASSIGNED':
			messageParams = {
				personName:
					params.personName === 'RECORD_DELETED'
						? intl.formatMessage({id: 'task_activity_log.record-deleted'})
						: params.personName,
			};
			break;
		default:
			break;
	}
	return (
		<div>
			<FormattedMessage id={'task_activity_log.' + eventType} values={messageParams} />
		</div>
	);
}
